import { useCallback } from 'react'
import { MobileForm } from '@mobileTypes'
import {
  useCopyMobileFormMutation,
  useCreateMobileFormMutation,
  useDeleteMobileFormMutation,
  useFetchAllMobileFormsQuery,
  useUpdateMobileFormMutation,
} from '../services'

export const useMobileForms = () => {
  const { data: forms = [], isLoading: loading } = useFetchAllMobileFormsQuery()
  const [createMobileForm] = useCreateMobileFormMutation()
  const [updateMobileForm] = useUpdateMobileFormMutation()
  const [deleteMobileForm] = useDeleteMobileFormMutation()
  const [copyMobileForm] = useCopyMobileFormMutation()

  const createForm = useCallback(
    (formData: Omit<MobileForm, 'id'>): Promise<MobileForm> =>
      createMobileForm(formData).unwrap(),
    [createMobileForm]
  )

  const updateForm = useCallback(
    (id: string, formData: Partial<MobileForm>): Promise<MobileForm> => {
      const numericId = Number(id)
      const existing = forms.find(form => form.id === numericId)
      const merged: MobileForm = {
        ...(existing ?? ({} as MobileForm)),
        ...formData,
        id: numericId,
      }

      return updateMobileForm(merged).unwrap()
    },
    [forms, updateMobileForm]
  )

  const deleteForm = useCallback(
    async (id: string | number): Promise<void> => {
      await deleteMobileForm(String(id)).unwrap()
    },
    [deleteMobileForm]
  )

  const getFormById = useCallback(
    (id: string): MobileForm | undefined => forms.find(form => form.id === Number(id)),
    [forms]
  )

  const copyForm = useCallback(
    async (id: string | number, newTitle: string, newInternalId: string): Promise<MobileForm | null> => {
      try {
        return await copyMobileForm({ id: String(id), title: newTitle, internalId: newInternalId }).unwrap()
      } catch {
        return null
      }
    },
    [copyMobileForm]
  )

  return {
    forms,
    loading,
    createForm,
    updateForm,
    deleteForm,
    getFormById,
    copyForm,
  }
}
